import { Button, ButtonTypes, Input, Modal } from "@clintonelec/react-storybook";
import type { ChangeEvent, FormEvent, PropsWithChildren } from "react";
import { memo, useRef, useState } from "react";
import "Components/ConfigLayout/Camera/OverlaySettings/UploadModal/UploadModal.less";

type UploadModalProps = {
	restrictions: string;
	uploadFile: (name: string, dataUrl: string) => void;
	validateFile: (file: File) => Promise<boolean>;
};

function UploadModal(props: PropsWithChildren<UploadModalProps>) {
	const { uploadFile, children, restrictions, validateFile } = props;
	const [ selectedFile, setSelectedFile ] = useState<File | null>(null);
	const [ visible, setVisible ] = useState(false);
	const fileSelectRef = useRef<HTMLInputElement>(null);

	const resetState = () => {
		setSelectedFile(null);
	};

	const handleVisibilityChange = (newVisible: boolean) => {
		setVisible(newVisible);
	};

	const handleSelectedFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
		if (!event.target.files || event.target.files.length === 0) {
			return;
		}

		const file = event.target.files[ 0 ];
		const isValidFile = await validateFile(file);

		if (isValidFile) {
			setSelectedFile(file);
		}
	};

	const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		event.stopPropagation();

		if (!event.currentTarget?.checkValidity() || !selectedFile) {
			return;
		}

		const reader = new FileReader();

		reader.readAsDataURL(selectedFile);

		reader.onload = () => {
			const imageFileString = reader.result as string;

			uploadFile(selectedFile.name, imageFileString);
		};

		setVisible(false);
	};

	const handleFileSelectorClick = () => {
		fileSelectRef.current?.click();
	};

	const modalContent = (
		<form className="upload-modal" onSubmit={ handleFormSubmit } noValidate>
			<span>
				{ restrictions }
			</span>
			<div className="file-selector">
				<Button
					onClick={ handleFileSelectorClick }
					htmlType="button"
					type={ ButtonTypes.TERTIARY }
				>
					Choose File
				</Button>
				<input
					hidden
					onChange={ handleSelectedFileChange }
					ref={ fileSelectRef }
					type="file"
					accept="image/png,image/jpg"
				/>
				<Input
					disabled
					value={ selectedFile?.name }
				/>
			</div>
			<Button
				className="upload-button"
				disabled={ selectedFile === null }
				fill={ true }
				type={ ButtonTypes.TERTIARY }
			>
				Upload
			</Button>
		</form>
	);

	return (
		<Modal
			title="Add Image"
			modalContent={ modalContent }
			afterClose={ resetState }
			onVisibilityChange={ handleVisibilityChange }
			visible={ visible }
			width={ 400 }
		>
			{ children }
		</Modal>
	);
}

export default memo(UploadModal);
